import type { Bundle, Practitioner, Reference } from '@medplum/fhirtypes'
import {
  clearEntryDecision,
  setEntryDecision,
} from '@seen/core/queued-edits/decisions'
import type { ResourceDecision } from '@seen/core/queued-edits/model'
import { useCallback, useRef } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'

// `undefined` clears the decision for an entry.
type DesiredDecision = ResourceDecision | undefined

const MAX_CONFLICT_RETRIES = 3

// Serialized, coalescing writer for review decisions on the proposal Bundle.
// Each toggle persists onto the Bundle's entry extensions, but a burst of clicks
// must not stampede the server with racing version-tested writes. Strategy:
//
//   - Hold the latest desired decision per entry in a ref map (coalesce —
//     rapid flip-flops collapse to the final value before a write lands).
//   - Run at most one write at a time (a promise chain), draining the pending
//     map each pass.
//   - Each pass re-reads the latest Bundle, applies the batch's decisions to it,
//     and updates with optimistic concurrency (If-Match on meta.versionId). On a
//     version conflict, re-read and re-apply — decisions are idempotent intent,
//     so replaying the batch against fresh state never clobbers another entry.
//
// (The Bundle has no ExtendedResource wrapper, so this can't use
// patchResourceInstance; the read-mutate-update loop is the plain-resource
// equivalent of its refetch-on-conflict behavior.)
//
// Best-effort: the in-memory decision state is the source of truth for the live
// session; a failed write only costs durability, surfaced via onError.
export function useBundleDecisionWriter(
  bundle: Bundle,
  decidedBy: Reference<Practitioner>,
  onError?: (cause: unknown) => void
): (entry: string, decision: DesiredDecision) => void {
  const medplum = useMedplum()
  const bundleId = bundle.id ?? ''
  // Pending desired values awaiting a write, keyed by entry handle.
  const pendingRef = useRef<Map<string, DesiredDecision>>(new Map())
  // The in-flight drain, so we chain rather than race.
  const drainingRef = useRef<Promise<void> | null>(null)

  const applyBatch = useCallback(
    async (batch: [string, DesiredDecision][]) => {
      // Re-read → apply → update each pass. updateResource sends If-Match from
      // meta.versionId, so a concurrent write fails the precondition; we retry
      // by re-reading and re-applying. The mutation is idempotent intent, so
      // replaying the same batch against fresh state is always safe — a retry on
      // any transient failure costs only a redundant read.
      for (let attempt = 0; attempt < MAX_CONFLICT_RETRIES; attempt++) {
        const latest = await medplum.readResource('Bundle', bundleId)
        for (const [entry, decision] of batch) {
          if (decision) {
            setEntryDecision(latest, entry, decision, decidedBy)
          } else {
            clearEntryDecision(latest, entry)
          }
        }
        try {
          await medplum.updateResource(latest)
          return
        } catch (error) {
          if (attempt === MAX_CONFLICT_RETRIES - 1) {
            throw error
          }
        }
      }
    },
    [medplum, bundleId, decidedBy]
  )

  const drain = useCallback(async () => {
    while (pendingRef.current.size > 0) {
      // Snapshot and clear — anything that arrives mid-write stays pending for
      // the next pass.
      const batch = [...pendingRef.current.entries()]
      pendingRef.current = new Map()
      try {
        await applyBatch(batch)
      } catch (error) {
        onError?.(error)
      }
    }
  }, [applyBatch, onError])

  return useCallback(
    (entry: string, decision: DesiredDecision) => {
      if (!bundleId) {
        return
      }
      pendingRef.current.set(entry, decision)
      if (!drainingRef.current) {
        drainingRef.current = drain().finally(() => {
          drainingRef.current = null
        })
      }
    },
    [drain, bundleId]
  )
}
